// src/pages/VolunteerEvents.js
import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc,
  orderBy,
  Timestamp 
} from 'firebase/firestore';

const VolunteerEvents = ({ currentUser }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('upcoming'); // upcoming, past, all
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    date: '',
    startTime: '',
    endTime: '',
    location: '',
    volunteersNeeded: 1,
    skills: [],
    isRecurring: false,
    recurringPattern: 'weekly', // weekly, monthly
    recurringEnd: '',
    instructions: '',
    contactPerson: '',
    contactEmail: '',
    contactPhone: ''
  });

  const skillOptions = [
    'Childcare', 'Youth Ministry', 'Music/Worship', 'Teaching', 'Administration',
    'Event Setup', 'Food Service', 'Technology', 'Cleaning', 'Greeting/Hospitality',
    'Security', 'Transportation', 'Photography', 'Audio/Visual', 'Maintenance'
  ];

  useEffect(() => {
    if (currentUser?.tenantId) {
      loadEvents();
    }
  }, [currentUser, filter]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      
      let q = query(
        collection(db, 'volunteerEvents'),
        where('tenantId', '==', currentUser.tenantId),
        orderBy('date', 'desc')
      );
      
      const snapshot = await getDocs(q);
      const eventsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setEvents(eventsData);
      
    } catch (err) {
      console.error('Error loading events:', err);
      setError('Failed to load events. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      if (name === 'skills') {
        setNewEvent(prev => ({
          ...prev,
          skills: checked 
            ? [...prev.skills, value]
            : prev.skills.filter(skill => skill !== value)
        }));
      } else {
        setNewEvent(prev => ({
          ...prev,
          [name]: checked
        }));
      }
    } else {
      setNewEvent(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  setLoading(true);
  setError('');

  try {
    const eventDate = new Date(`${newEvent.date}T${newEvent.startTime}`);
    
    const eventData = {
      title: newEvent.title,
      description: newEvent.description,
      date: Timestamp.fromDate(eventDate),
      startTime: newEvent.startTime,
      endTime: newEvent.endTime,
      location: newEvent.location,
      volunteersNeeded: parseInt(newEvent.volunteersNeeded),
      skills: newEvent.skills,
      isRecurring: newEvent.isRecurring,
      recurringPattern: newEvent.recurringPattern,
      recurringEnd: newEvent.recurringEnd,
      instructions: newEvent.instructions,
      contactPerson: newEvent.contactPerson,
      contactEmail: newEvent.contactEmail,
      contactPhone: newEvent.contactPhone,
      tenantId: currentUser.tenantId,
      status: 'active'
    };

    if (editingEvent) {
      // When updating, preserve existing fields like volunteersAssigned
      const updateData = {
        ...eventData,
        updatedAt: Timestamp.now(),
        updatedBy: currentUser.uid
      };
      // Don't overwrite volunteersAssigned or createdAt/createdBy
      delete updateData.tenantId; // Don't change tenant
      
      await updateDoc(doc(db, 'volunteerEvents', editingEvent.id), updateData);
    } else {
      // Only add these fields for new events
      const newEventData = {
        ...eventData,
        createdBy: currentUser.uid,
        createdAt: Timestamp.now(),
        volunteersAssigned: []
      };
      
      const docRef = await addDoc(collection(db, 'volunteerEvents'), newEventData);
      
      if (newEvent.isRecurring && newEvent.recurringEnd) {
        await createRecurringEvents(newEventData, docRef.id);
      }
    }

    // Reset form
    setNewEvent({
      title: '',
      description: '',
      date: '',
      startTime: '',
      endTime: '',
      location: '',
      volunteersNeeded: 1,
      skills: [],
      isRecurring: false,
      recurringPattern: 'weekly',
      recurringEnd: '',
      instructions: '',
      contactPerson: '',
      contactEmail: '',
      contactPhone: ''
    });
    
    setShowCreateForm(false);
    setEditingEvent(null);
    await loadEvents();
    
  } catch (err) {
    console.error('Error saving event:', err);
    setError('Failed to save event. Please try again.');
  } finally {
    setLoading(false);
  }
};

  const createRecurringEvents = async (baseEvent, parentEventId) => {
  const startDate = baseEvent.date.toDate();
  const endDate = new Date(newEvent.recurringEnd);
  const events = [];

  let currentDate = new Date(startDate);
  
  while (currentDate <= endDate) {
    if (newEvent.recurringPattern === 'weekly') {
      currentDate.setDate(currentDate.getDate() + 7);
    } else if (newEvent.recurringPattern === 'monthly') {
      currentDate.setMonth(currentDate.getMonth() + 1);
    }
    
    if (currentDate <= endDate) {
      events.push({
        ...baseEvent,
        date: Timestamp.fromDate(new Date(currentDate)),
        isRecurringInstance: true,
        parentEventId: parentEventId // ✅ FIXED: Use the passed document ID
      });
    }
  }

  // Create all recurring events
  for (const event of events) {
    await addDoc(collection(db, 'volunteerEvents'), event);
  }
};

  const handleEdit = (event) => {
  console.log('Edit clicked for event:', event);
  
  try {
    // Handle the date - it's a Firestore Timestamp
    let eventDate;
    if (event.date && event.date.toDate) {
      eventDate = event.date.toDate();
    } else if (event.date) {
      eventDate = new Date(event.date);
    } else {
      eventDate = new Date();
    }
    
    // Set the form data
    setNewEvent({
      title: event.title || '',
      description: event.description || '',
      date: eventDate.toISOString().split('T')[0],
      startTime: event.startTime || '09:00',
      endTime: event.endTime || '',
      location: event.location || '',
      volunteersNeeded: event.volunteersNeeded || 1,
      skills: event.skills || [],
      isRecurring: event.isRecurring || false,
      recurringPattern: event.recurringPattern || 'weekly',
      recurringEnd: event.recurringEnd || '',
      instructions: event.instructions || '',
      contactPerson: event.contactPerson || '',
      contactEmail: event.contactEmail || '',
      contactPhone: event.contactPhone || ''
    });
    
    setEditingEvent(event);
    setShowCreateForm(true);
    
    // Scroll to top of page to show the edit form
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
  } catch (error) {
    console.error('Error in handleEdit:', error);
    setError(`Failed to edit event: ${error.message}`);
  }
};

  const handleDelete = async (eventId) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      try {
        await deleteDoc(doc(db, 'volunteerEvents', eventId));
        await loadEvents();
      } catch (err) {
        console.error('Error deleting event:', err);
        setError('Failed to delete event. Please try again.');
      }
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'No date';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const filteredEvents = events.filter(event => {
    const eventDate = event.date.toDate();
    const now = new Date();
    
    switch (filter) {
      case 'upcoming':
        return eventDate >= now;
      case 'past':
        return eventDate < now;
      default:
        return true;
    }
  });

  if (loading && events.length === 0) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '18px', color: '#666' }}>Loading events...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '30px',
        flexWrap: 'wrap',
        gap: '15px'
      }}>
        <div>
          <h1 style={{ margin: '0 0 10px 0', fontSize: '28px', fontWeight: 'bold', color: '#333' }}>
            Volunteer Events
          </h1>
          <p style={{ margin: 0, color: '#666', fontSize: '16px' }}>
            Create and manage volunteer opportunities
          </p>
        </div>
        
        <button
          onClick={() => {
            setShowCreateForm(true);
            setEditingEvent(null);
            setNewEvent({
              title: '',
              description: '',
              date: '',
              startTime: '',
              endTime: '',
              location: '',
              volunteersNeeded: 1,
              skills: [],
              isRecurring: false,
              recurringPattern: 'weekly',
              recurringEnd: '',
              instructions: '',
              contactPerson: '',
              contactEmail: '',
              contactPhone: ''
            });
          }}
          style={{
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: 'pointer'
          }}
        >
          + Create Event
        </button>
      </div>

      {error && (
        <div style={{ 
          backgroundColor: '#f8d7da', 
          color: '#721c24', 
          padding: '12px', 
          borderRadius: '8px', 
          marginBottom: '20px',
          border: '1px solid #f5c6cb'
        }}>
          {error}
        </div>
      )}

      {/* Filter */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <label style={{ fontWeight: 'bold', color: '#333' }}>Filter:</label>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          >
            <option value="upcoming">Upcoming Events ({events.filter(e => e.date.toDate() >= new Date()).length})</option>
            <option value="past">Past Events ({events.filter(e => e.date.toDate() < new Date()).length})</option>
            <option value="all">All Events ({events.length})</option>
          </select>
        </div>
      </div>

      {/* Create/Edit Form */}
      {showCreateForm && (
        <div style={{ 
          backgroundColor: 'white', 
          border: '1px solid #dee2e6', 
          borderRadius: '8px', 
          padding: '24px',
          marginBottom: '30px'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold', color: '#333' }}>
              {editingEvent ? 'Edit Event' : 'Create New Event'}
            </h3>
            <button
              onClick={() => {
                setShowCreateForm(false);
                setEditingEvent(null);
              }}
              style={{
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Basic Info */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Event Title *</label>
                <input
                  type="text"
                  name="title"
                  value={newEvent.title}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., Sunday School Helper"
                  style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '16px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Location *</label>
                <input
                  type="text"
                  name="location"
                  value={newEvent.location}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., Children's Wing Room 5"
                  style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '16px' }}
                />
              </div>
            </div>

            {/* Date and Time */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '20px', marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Date *</label>
                <input
                  type="date"
                  name="date"
                  value={newEvent.date}
                  onChange={handleInputChange}
                  required
                  style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '16px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Start Time *</label>
                <input
                  type="time"
                  name="startTime"
                  value={newEvent.startTime}
                  onChange={handleInputChange}
                  required
                  style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '16px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>End Time</label>
                <input
                  type="time"
                  name="endTime"
                  value={newEvent.endTime}
                  onChange={handleInputChange}
                  style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '16px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Volunteers Needed *</label>
                <input
                  type="number"
                  name="volunteersNeeded"
                  value={newEvent.volunteersNeeded}
                  onChange={handleInputChange}
                  required
                  min="1"
                  style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '16px' }}
                />
              </div>
            </div>

            {/* Description */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Description</label>
              <textarea
                name="description"
                value={newEvent.description}
                onChange={handleInputChange}
                rows={3}
                placeholder="Describe what volunteers will be doing..."
                style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '16px', resize: 'vertical' }}
              />
            </div>

            {/* Skills Required */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>Skills Required (optional)</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                {skillOptions.map(skill => (
                  <label key={skill} style={{ display: 'flex', alignItems: 'center', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      name="skills"
                      value={skill}
                      checked={newEvent.skills.includes(skill)}
                      onChange={handleInputChange}
                      style={{ marginRight: '8px' }}
                    />
                    <span style={{ fontSize: '14px' }}>{skill}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Recurring Options */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', marginBottom: '15px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  name="isRecurring"
                  checked={newEvent.isRecurring}
                  onChange={handleInputChange}
                  style={{ marginRight: '10px' }}
                />
                <span style={{ fontWeight: 'bold' }}>Make this a recurring event</span>
              </label>

              {newEvent.isRecurring && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginLeft: '30px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Repeat Pattern</label>
                    <select
                      name="recurringPattern"
                      value={newEvent.recurringPattern}
                      onChange={handleInputChange}
                      style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '16px' }}
                    >
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Repeat Until</label>
                    <input
                      type="date"
                      name="recurringEnd"
                      value={newEvent.recurringEnd}
                      onChange={handleInputChange}
                      style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '16px' }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Contact Info */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Contact Person</label>
                <input
                  type="text"
                  name="contactPerson"
                  value={newEvent.contactPerson}
                  onChange={handleInputChange}
                  placeholder="Point of contact"
                  style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '16px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Contact Email</label>
                <input
                  type="email"
                  name="contactEmail"
                  value={newEvent.contactEmail}
                  onChange={handleInputChange}
                  placeholder="contact@example.com"
                  style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '16px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Contact Phone</label>
                <input
                  type="tel"
                  name="contactPhone"
                  value={newEvent.contactPhone}
                  onChange={handleInputChange}
                  placeholder="(555) 123-4567"
                  style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '16px' }}
                />
              </div>
            </div>

            {/* Instructions */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Special Instructions</label>
              <textarea
                name="instructions"
                value={newEvent.instructions}
                onChange={handleInputChange}
                rows={2}
                placeholder="Any special instructions for volunteers..."
                style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '16px', resize: 'vertical' }}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                backgroundColor: loading ? '#e9ecef' : '#28a745',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '4px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Saving...' : (editingEvent ? 'Update Event' : 'Create Event')}
            </button>
          </form>
        </div>
      )}

      {/* Events List */}
      {filteredEvents.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          backgroundColor: 'white', 
          borderRadius: '8px',
          border: '1px solid #dee2e6'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>📅</div>
          <div style={{ fontSize: '18px', color: '#666', marginBottom: '10px' }}>
            No {filter !== 'all' ? filter : ''} events found
          </div>
          <div style={{ fontSize: '14px', color: '#999', marginBottom: '20px' }}>
            Create your first volunteer event to get started!
          </div>
          <button
            onClick={() => setShowCreateForm(true)}
            style={{
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            Create First Event
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {filteredEvents.map(event => (
            <div key={event.id} style={{ 
              backgroundColor: 'white', 
              borderRadius: '8px', 
              border: '1px solid #dee2e6',
              padding: '24px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: 'bold', color: '#333' }}>
                    {event.title}
                  </h3>
                  <div style={{ fontSize: '16px', color: '#666', marginBottom: '8px' }}>
                    📅 {formatDate(event.date)} at {formatTime(event.date)}
                    {event.endTime && ` - ${event.endTime}`}
                  </div>
                  <div style={{ fontSize: '16px', color: '#666', marginBottom: '8px' }}>
                    📍 {event.location}
                  </div>
                  <div style={{ fontSize: '16px', color: '#666' }}>
                    👥 {event.volunteersAssigned?.length || 0} / {event.volunteersNeeded} volunteers
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => handleEdit(event)}
                    style={{
                      backgroundColor: '#007bff',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '4px',
                      fontSize: '14px',
                      cursor: 'pointer'
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(event.id)}
                    style={{
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '4px',
                      fontSize: '14px',
                      cursor: 'pointer'
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>

              {event.description && (
                <div style={{ marginBottom: '12px', color: '#555', lineHeight: '1.5' }}>
                  {event.description}
                </div>
              )}

              {event.skills?.length > 0 && (
                <div style={{ marginBottom: '12px' }}>
                  <strong>Skills needed: </strong>
                  {event.skills.join(', ')}
                </div>
              )}

              {event.instructions && (
                <div style={{ 
                  padding: '12px', 
                  backgroundColor: '#f8f9fa', 
                  borderRadius: '4px',
                  fontSize: '14px',
                  color: '#555'
                }}>
                  <strong>Instructions: </strong>{event.instructions}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VolunteerEvents;